import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import clss from 'classnames'

export type Selected = string | number | Array<string | number>
export interface OptProps {
  label: string | number | JSX.Element
  value: string | number
}
interface Poprs {
  options: Array<OptProps>
  className?: string
  placeholder?: string
  search?: boolean
  selected?: Selected
  onChange?: (keys: Selected) => void
}

const SelectLayout = styled.div`
  position: relative;
  min-width: 150px;
  .select-box {
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: 4px;
    border: 1px solid #3A3939;
    height: 48px;
    padding: 0 12px;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    color: #FFFFFF;
    &:focus, &:active {
      border-color: #E0C286;
    }
  }
  .placeholder {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #6B6B6B;
    position: absolute;
    left: 12px;
    width: calc(100% - 30px);
  }
  .select-opt {
    transform-origin: top;
    display: none;
    z-index: 2;
    position: absolute;
    top: calc(100% + 5px);
    padding: 8px;
    left: 0;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    color: #FFFFFF;
    overflow: hidden;
    width: 100%;
    background: #1A1919;
    border: 1px solid #3A3939;
    border-radius: 4px;
    max-height: 176px;
    overflow-y: auto;
    .opt-item {
      width: 100%;
      padding: 7px 8px;
      height: 32px;
      cursor: pointer;
      border-radius: 4px;
      &:hover, &.active {
        background: rgba(224, 194, 134, 0.08);
      }
    }
    &.show {
      display: block;
    }
    &.hide {
      display: none;
    }
  }
  .select-icon {
    position: absolute;
    right: 15px;
    width: 6px;
    height: 6px;
    transition: transform 0.3s ease-in-out;
    border-bottom: 1px solid #ffffff;
    border-right: 1px solid #ffffff;
    transform: rotate(45deg);
    top: 20px;
    &.active {
      transform: rotate(225deg);
      border-color: #E0C286;
      top: 21px;
    }
  }
`
const Select = (props: Poprs) => {
  const {
    selected,
    options,
    onChange,
    className,
    placeholder,
  } = props || {}
  const [current, setCurrent] = useState<Array<OptProps>>([])
  const [optState, setOptState] = useState<string>('')
  const [singleInputShow, setSingleInputShow] = useState<boolean>(false)

  const showEmpty = useMemo(() => {
    return !options.length
  }, [options])

  // 触发选择
  const onSelect = useCallback(
    (opt: OptProps) => {
      let newCurrent = []
      newCurrent = [opt]
      onChange && onChange((newCurrent[0] && newCurrent[0].value) || '')
      setSingleInputShow(false)
      setCurrent(newCurrent)
    },
    [options, current, onChange],
  )
  // 设置默认值
  useEffect(() => {
    if (selected) {
      const t = options.find(o => o.value === selected)
      if (t) {
        setCurrent([t])
      }
    }
  }, [options, selected])
  // 是否选中
  const isActive = useCallback(
    value => {
      return current.some(c => c.value === value) ? 'active' : ''
    },
    [options, current],
  )
  // search为false由select来控制option状态
  const onSelectBlur = useCallback(() => {
    setSingleInputShow(false)
    // 防止blur比click提前触发，导致点击事件不触发
    setTimeout(() => {
      setOptState('hide')
    }, 150)
  }, [singleInputShow])
  // isMultiSearch触发input自动聚焦
  const onSelectFocus = useCallback(() => {
    setOptState('show')
  }, [])


  return (
    <SelectLayout className={className}>
      <div
        className="select-box"
        tabIndex={-1}
        onBlur={onSelectBlur}
        onFocus={onSelectFocus}>
        {/* selected */}
        {current.map(c => (
          <span className="value" key={c.value}>
            {c.label}
          </span>
        ))}
        {/* placeholder */}
        {!current.length && !singleInputShow && placeholder && (
          <>
            <span className="placeholder font-[500]">{placeholder}</span>
          </>
        )}
      </div>
      <SelectIcon className={clss({ 'active': optState === 'show' })} />
      {/* options */}
      <ul className={`select-opt ${optState}`}>
        {/* empty */}
        {showEmpty ? (
          <span className="text-desc px-[10px] py-[10px] block text-center">no data</span>
        ) : (
          options.map(o => (
            <li className={`opt-item ${isActive(o.value)}`} onClick={() => onSelect(o)} key={o.value}>
              {o.label}
            </li>
          ))
        )}
      </ul>
    </SelectLayout>
  )
}


const SelectIcon = ({ className }: { className: string }) => {
  return (
    <div
      className={clss(className, 'select-icon')}
    />
  )
}

export default Select
